import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import "../../css/ProductPage.css";
import { useSearch } from "../../context/searchContext";
import axios from "axios";
import { useAuth } from "../../context/auth";
import { toast } from "react-hot-toast";

export default function Search() {
  const [values] = useSearch();
  const [auth] = useAuth();
  const [wishlist, setWishlist] = useState([]);

  const [sortBy, setSortBy] = useState("relevance");
  const [onSaleOnly, setOnSaleOnly] = useState(false);
  const [maxPrice, setMaxPrice] = useState("");

  const apiUrl = `${process.env.REACT_APP_API}`;
  const results = (values && values.results) || [];

  const getToken = () => {
    if (auth?.token) return auth.token;
    try {
      const local = JSON.parse(localStorage.getItem("auth"));
      return local?.token || "";
    } catch {
      return "";
    }
  };

  const effectivePrice = (p) => (p.salePrice > 0 ? p.salePrice : p.productPrice);

  /* --------------------------------- Wishlist -------------------------------- */

  const fetchWishlist = async () => {
    try {
      const userId = auth?.currentUser?.id;
      if (!userId) return;
      const response = await axios.get(`${apiUrl}/wishlist`, {
        headers: { Authorization: getToken() },
      });
      setWishlist(response.data.products || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchWishlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.currentUser?.id]);

  const isInWishlist = (productId) =>
      wishlist.some((item) => item._id === productId);

  const addToWishlist = async (productId) => {
    try {
      await axios.post(
          `${apiUrl}/wishlist/add`,
          { productId },
          { headers: { Authorization: getToken() } }
      );
      toast.success("Added to Wishlist");
      await fetchWishlist();
    } catch (error) {
      console.error(error);
      toast.error("Couldn't add to wishlist");
    }
  };

  const removeFromWishlist = async (productId) => {
    try {
      await axios.delete(`${apiUrl}/wishlist/delete/${productId}`, {
        headers: { Authorization: getToken() },
      });
      setWishlist((prev) => prev.filter((p) => p._id !== productId));
      toast.success("Removed from Wishlist");
      await fetchWishlist();
    } catch (err) {
      console.error(err.message);
      toast.error("Couldn't remove from wishlist");
    }
  };

  const handleHeartClick = (productId) => {
    if (!localStorage.getItem("auth")) {
      toast.error("Please log in to use your wishlist");
      return;
    }
    if (isInWishlist(productId)) removeFromWishlist(productId);
    else addToWishlist(productId);
  };

  /* ----------------------------- Filter + sort ------------------------------ */

  const visibleResults = useMemo(() => {
    let list = [...results];

    if (onSaleOnly) list = list.filter((p) => p.salePrice > 0);
    if (maxPrice !== "" && !isNaN(maxPrice)) {
      list = list.filter((p) => effectivePrice(p) <= Number(maxPrice));
    }

    switch (sortBy) {
      case "priceLow":
        list.sort((a, b) => effectivePrice(a) - effectivePrice(b));
        break;
      case "priceHigh":
        list.sort((a, b) => effectivePrice(b) - effectivePrice(a));
        break;
      case "nameAsc":
        list.sort((a, b) =>
            (a.productName || "").localeCompare(b.productName || "")
        );
        break;
      default:
        break;
    }
    return list;
  }, [results, onSaleOnly, maxPrice, sortBy]);

  const clearFilters = () => {
    setOnSaleOnly(false);
    setMaxPrice("");
    setSortBy("relevance");
  };

  const hasResults = results.length > 0;
  const hasVisible = visibleResults.length > 0;
  const filtersActive = onSaleOnly || maxPrice !== "" || sortBy !== "relevance";

  /* --------------------------------- Render --------------------------------- */

  return (
      <>
        <Header />
        <div className="kc-search">
          <div className="kc-search-inner">
            <header className="kc-head">
              <p className="kc-eyebrow">Search</p>
              <h1 className="kc-title">
                {hasResults ? "Search results" : "Nothing found"}
              </h1>
              <p className="kc-subtitle">
                {hasResults
                    ? `${results.length} ${
                        results.length === 1 ? "piece" : "pieces"
                    } found`
                    : "We couldn't find anything for that search."}
              </p>
            </header>

            {hasResults && (
                <div className="kc-toolbar">
                  <div className="kc-filters">
                    <label className="kc-check">
                      <input
                          type="checkbox"
                          checked={onSaleOnly}
                          onChange={(e) => setOnSaleOnly(e.target.checked)}
                      />
                      <span>On sale</span>
                    </label>

                    <div className="kc-price">
                      <span>Max&nbsp;$</span>
                      <input
                          type="number"
                          min="0"
                          value={maxPrice}
                          onChange={(e) => setMaxPrice(e.target.value)}
                          placeholder="Any"
                      />
                    </div>

                    {filtersActive && (
                        <button className="kc-clear" onClick={clearFilters}>
                          Clear
                        </button>
                    )}
                  </div>

                  <div className="kc-sort">
                    <label htmlFor="sortBy">Sort</label>
                    <select
                        id="sortBy"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="relevance">Relevance</option>
                      <option value="priceLow">Price: Low to High</option>
                      <option value="priceHigh">Price: High to Low</option>
                      <option value="nameAsc">Name: A–Z</option>
                    </select>
                  </div>
                </div>
            )}

            {hasResults && (
                <p className="kc-showing">
                  Showing {visibleResults.length} of {results.length}
                </p>
            )}

            {hasResults && !hasVisible && (
                <div className="kc-empty">
                  <p>No products match your filters.</p>
                  <button className="kc-btn" onClick={clearFilters}>
                    Clear filters
                  </button>
                </div>
            )}

            {!hasResults && (
                <div className="kc-empty">
                  <p>Try a different keyword, or browse our latest arrivals.</p>
                  <Link to="/" className="kc-btn">
                    Browse products
                  </Link>
                </div>
            )}

            {hasVisible && (
                <div className="kc-grid">
                  {visibleResults.map((product) => (
                      <article className="kc-card" key={product._id}>
                        <div className="kc-media">
                          <Link
                              to={`/products/${product._id}`}
                              className="kc-media-link"
                          >
                            <img
                                src={product.images.front}
                                alt={product.productName}
                                loading="lazy"
                            />
                          </Link>

                          {product.salePrice > 0 && (
                              <span className="kc-badge">
                        {Math.round(
                            ((product.productPrice - product.salePrice) /
                                product.productPrice) *
                            100
                        )}
                                % off
                      </span>
                          )}

                          <button
                              aria-label={
                                isInWishlist(product._id)
                                    ? "Remove from wishlist"
                                    : "Add to wishlist"
                              }
                              className={`kc-heart ${
                                  isInWishlist(product._id) ? "is-active" : ""
                              }`}
                              onClick={() => handleHeartClick(product._id)}
                          >
                            <i className="fas fa-heart"></i>
                          </button>
                        </div>

                        <div className="kc-body">
                          <Link
                              to={`/products/${product._id}`}
                              className="kc-name"
                          >
                            {product.productName}
                          </Link>
                          <div className="kc-price-row">
                            {product.salePrice > 0 ? (
                                <>
                                  <span className="kc-sale">${product.salePrice}</span>
                                  <span className="kc-old">
                            ${product.productPrice}
                          </span>
                                </>
                            ) : (
                                <span className="kc-reg">${product.productPrice}</span>
                            )}
                          </div>
                        </div>
                      </article>
                  ))}
                </div>
            )}
          </div>
        </div>
        <Footer />

        <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600;700&display=swap');

        .kc-search { background: #ffffff; padding: 0 0 72px; }
        .kc-search-inner { max-width: 1240px; margin: 0 auto; padding: 44px 24px 0; }

        .kc-head { margin-bottom: 8px; }
        .kc-eyebrow {
          text-transform: uppercase; letter-spacing: 0.24em;
          font-size: 0.72rem; font-weight: 700; color: #c70044; margin: 0 0 10px;
        }
        .kc-title {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: clamp(2.3rem, 4.5vw, 3.4rem); font-weight: 600;
          color: #002E6A; margin: 0; letter-spacing: -0.01em; line-height: 1.02;
        }
        .kc-subtitle { color: #8a909b; font-size: 0.95rem; margin: 10px 0 0; }

        .kc-toolbar {
          display: flex; flex-wrap: wrap; gap: 16px;
          align-items: center; justify-content: space-between;
          padding: 16px 0; margin-top: 22px;
          border-top: 1px solid #ECEEF2; border-bottom: 1px solid #ECEEF2;
        }
        .kc-filters { display: flex; flex-wrap: wrap; align-items: center; gap: 20px; }
        .kc-check { display: inline-flex; align-items: center; gap: 8px; margin: 0; cursor: pointer; font-size: 0.9rem; color: #2b2f36; }
        .kc-check input { width: 16px; height: 16px; accent-color: #c70044; cursor: pointer; }
        .kc-price { display: inline-flex; align-items: center; gap: 7px; font-size: 0.9rem; color: #2b2f36; }
        .kc-price input {
          width: 86px; padding: 8px 11px; border: 1px solid #DEE1E7; border-radius: 10px;
          font-size: 0.88rem; outline: none; transition: border-color .15s, box-shadow .15s;
        }
        .kc-price input:focus { border-color: #002E6A; box-shadow: 0 0 0 3px rgba(0,46,106,.10); }
        .kc-clear { background: none; border: none; color: #c70044; font-weight: 600; font-size: 0.85rem; cursor: pointer; }
        .kc-clear:hover { text-decoration: underline; }
        .kc-sort { display: inline-flex; align-items: center; gap: 10px; }
        .kc-sort label { font-size: 0.74rem; text-transform: uppercase; letter-spacing: .14em; color: #9aa1ad; margin: 0; }
        .kc-sort select {
          padding: 9px 14px; border: 1px solid #DEE1E7; border-radius: 10px;
          font-size: 0.88rem; background: #fff; cursor: pointer; outline: none; color: #2b2f36;
          transition: border-color .15s, box-shadow .15s;
        }
        .kc-sort select:focus { border-color: #002E6A; box-shadow: 0 0 0 3px rgba(0,46,106,.10); }

        .kc-showing { color: #aab0ba; font-size: 0.8rem; letter-spacing: .03em; margin: 16px 0 26px; }

        .kc-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 34px 24px; }
        @media (max-width: 992px) { .kc-grid { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 700px) { .kc-grid { grid-template-columns: repeat(2, 1fr); gap: 26px 16px; } }

        .kc-card { display: flex; flex-direction: column; }
        .kc-media {
          position: relative; aspect-ratio: 3 / 4; overflow: hidden;
          border-radius: 14px; background: #F6F5F3;
        }
        .kc-media-link { display: block; width: 100%; height: 100%; }
        .kc-media img {
          width: 100%; height: 100%; object-fit: cover; object-position: center;
          transition: transform .7s cubic-bezier(.2,.7,.2,1);
        }
        .kc-card:hover .kc-media img { transform: scale(1.06); }

        .kc-badge {
          position: absolute; top: 12px; left: 12px; background: #c70044; color: #fff;
          font-size: 0.68rem; font-weight: 700; letter-spacing: .04em;
          padding: 5px 11px; border-radius: 999px; text-transform: uppercase;
        }
        .kc-heart {
          position: absolute; top: 10px; right: 10px; width: 40px; height: 40px;
          border-radius: 50%; display: grid; place-items: center;
          background: rgba(255,255,255,.92); border: none; cursor: pointer;
          color: #b9bec7; font-size: 0.98rem; box-shadow: 0 2px 10px rgba(0,0,0,.12);
          transition: transform .15s, color .15s;
        }
        .kc-heart:hover { transform: scale(1.08); color: #c70044; }
        .kc-heart.is-active { color: #c70044; }
        .kc-heart.is-active i { animation: kc-pop .3s ease; }
        @keyframes kc-pop { 0%{transform:scale(.6)} 60%{transform:scale(1.25)} 100%{transform:scale(1)} }

        .kc-body { padding: 15px 4px 0; }
        .kc-name {
          color: #1f2630; font-weight: 600; font-size: 0.98rem; text-decoration: none;
          line-height: 1.35; display: -webkit-box; -webkit-line-clamp: 2;
          -webkit-box-orient: vertical; overflow: hidden; min-height: 2.6em;
          transition: color .15s;
        }
        .kc-name:hover { color: #c70044; }
        .kc-price-row { display: flex; align-items: baseline; gap: 10px; margin-top: 9px; }
        .kc-sale { color: #c70044; font-weight: 700; font-size: 1.04rem; }
        .kc-old { color: #b3b9c2; text-decoration: line-through; font-size: 0.85rem; }
        .kc-reg { color: #002E6A; font-weight: 700; font-size: 1.04rem; }

        .kc-empty { text-align: center; padding: 60px 16px; color: #6b7280; }
        .kc-empty p { margin-bottom: 18px; font-size: 1rem; }
        .kc-btn {
          display: inline-block; padding: 12px 30px; border-radius: 10px;
          background: #002E6A; color: #fff; text-decoration: none; border: none;
          font-weight: 600; font-size: 0.92rem; cursor: pointer; letter-spacing: .02em;
          transition: background .15s, transform .12s;
        }
        .kc-btn:hover { background: #c70044; }
        .kc-btn:active { transform: translateY(1px); }

        @media (prefers-reduced-motion: reduce) {
          .kc-media img, .kc-heart, .kc-heart.is-active i { transition: none; animation: none; }
        }
      `}</style>
      </>
  );
}